import { QueryResult } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { DialogueIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { InboxTaskType } from '@treams/treams-network';
import {
	Card,
	Container,
	Empty,
	InfiniteScroll,
	PAGE_CONTENT_WIDTH,
	Skeleton,
	Subheading,
} from '@treams/treams-ui';

import InboxTasksContainer from '../packages/kakapo-ui-application/pages/inbox/InboxTasksContainer';
import OutgoingFeedbackCard from './OutgoingFeedbackCard';

export default function OutgoingFeedbackList(props: {
	query: QueryResult<graphql.OutgoingFeedbackResponsesQuery>;
	userID: string;
}) {
	const { t } = useTranslation();

	const inboxQuery = graphql.useInboxTasksQuery();
	const hasOutstandingRequests = (inboxQuery.data?.viewer.inboxTasks ?? []).some(
		(task) => task.type === InboxTaskType.FeedbackRequestPending,
	);

	const givenHeading = hasOutstandingRequests ? (
		<Box mb={3}>
			<Subheading>{t('feedback.outgoing.given.title')}</Subheading>
		</Box>
	) : null;

	return (
		<Container mb={3} wide={PAGE_CONTENT_WIDTH}>
			<Box mt={4} mb={3}>
				<InboxTasksContainer
					type={InboxTaskType.FeedbackRequestPending}
					title={t('feedback.outgoing.outstanding.title')}
				/>
			</Box>

			{props.query.data ? (
				props.query.data.user.outgoingFeedbackResponses.edges.length > 0 ? (
					<>
						{givenHeading}
						<Card p={0}>
							{props.query.data.user.outgoingFeedbackResponses.edges
								.slice()
								.reverse()
								.map(({ node }) => (
									<OutgoingFeedbackCard
										key={node.id}
										userID={props.userID}
										response={node as graphql.FeedbackResponse}
									/>
								))}
						</Card>
						<InfiniteScroll query={props.query} path="user.outgoingFeedbackResponses" reverse>
							<Skeleton.Repeat count={10}>
								<OutgoingFeedbackCard.Skeleton />
							</Skeleton.Repeat>
						</InfiniteScroll>
					</>
				) : (
					<>
						{givenHeading}
						<Skeleton.Empty
							contents={
								<Empty
									icon={<DialogueIcon />}
									title={t('feedback.outgoing.empty.title')}
									description={t('feedback.outgoing.empty.description')}
								/>
							}
						>
							<Skeleton.Repeat count={3}>
								<OutgoingFeedbackCard.Skeleton />
							</Skeleton.Repeat>
						</Skeleton.Empty>
					</>
				)
			) : (
				<Skeleton.Repeat count={10}>
					<OutgoingFeedbackCard.Skeleton />
				</Skeleton.Repeat>
			)}
		</Container>
	);
}
